import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useAccessApi, Organization, EnhancedAuth0User, TeamMembership } from '../services/api';
import { InlineSpinner } from './LoadingSpinner';
import {
    useUpdateUserTeamRoleOrgAdmin,
    useRemoveUserFromTeamOrgAdmin,
    useSyncUserData,
} from '../hooks/useApi';
import { LoadingPage } from './LoadingSpinner';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from './ui/table';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { RoleBadge } from './RoleBadge';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Badge } from 'lucide-react';

// Helper function to get Pro role display name
const getProRoleName = (roleId: number): string => {
    switch (roleId) {
        case 1:
            return 'Pro Restricted';
        case 2:
            return 'Pro User';
        case 3:
            return 'Pro Approver';
        default:
            return 'Role Unknown';
    }
};

// Helper function to render user avatar
const renderUserAvatar = (name: string, hasNotLoggedIn = false) => (
    <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            hasNotLoggedIn ? 'bg-gray-100' : 'bg-neutral-200'
        }`}
    >
        <span
            className={`text-xs font-medium ${hasNotLoggedIn ? 'text-gray-400' : 'text-neutral-600'}`}
        >
            {name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()}
        </span>
    </div>
);

type TeamMemberUser = EnhancedAuth0User & {
    current_team_role_id?: number;
    current_team_role_name?: string;
};

const PRO_TEAM_ROLE_BUTTONS = [
    {
        roleId: 1,
        label: 'Restricted',
        activeClassName: 'border-orange-500 text-orange-900 bg-orange-200 shadow-md font-semibold',
        inactiveClassName:
            'border-orange-200 text-orange-400 bg-orange-25 opacity-60 hover:opacity-100 hover:text-orange-700 hover:border-orange-400 hover:bg-orange-100 hover:shadow-sm',
        title: 'Set as Pro Restricted',
    },
    {
        roleId: 2,
        label: 'User',
        activeClassName: 'border-blue-500 text-blue-900 bg-blue-200 shadow-md font-semibold',
        inactiveClassName:
            'border-blue-200 text-blue-400 bg-blue-25 opacity-60 hover:opacity-100 hover:text-blue-700 hover:border-blue-400 hover:bg-blue-100 hover:shadow-sm',
        title: 'Set as Pro User',
    },
    {
        roleId: 3,
        label: 'Approver',
        activeClassName: 'border-purple-500 text-purple-900 bg-purple-200 shadow-md font-semibold',
        inactiveClassName:
            'border-purple-200 text-purple-400 bg-purple-25 opacity-60 hover:opacity-100 hover:text-purple-700 hover:border-purple-400 hover:bg-purple-100 hover:shadow-sm',
        title: 'Set as Pro Approver',
    },
] as const;

type TeamMemberRoleButtonProps = {
    teamId: number;
    user: TeamMemberUser;
    roleId: number;
    label: string;
    activeClassName: string;
    inactiveClassName: string;
    title: string;
    loadingStates: Record<string, boolean>;
    getMemberLoadingKey: (
        member: { subject_id?: string | null; user_id?: string },
        action: string,
        roleId?: number,
    ) => string;
    onSetRole: (teamId: number, userId: string, newRoleId: number) => void;
    hasNotLoggedIn: boolean;
};

function TeamMemberRoleButton({
    teamId,
    user,
    roleId,
    label,
    activeClassName,
    inactiveClassName,
    title,
    loadingStates,
    getMemberLoadingKey,
    onSetRole,
    hasNotLoggedIn,
}: TeamMemberRoleButtonProps) {
    const userId = (user.subject_id || user.user_id) as string;
    const isActive = user.current_team_role_id === roleId;
    const loadingKey = getMemberLoadingKey(user, 'setRole', roleId);
    const isLoading = loadingStates[loadingKey];

    return (
        <button
            onClick={(e) => {
                e.preventDefault();
                onSetRole(teamId, userId, roleId);
                e.stopPropagation();
            }}
            disabled={(isLoading || hasNotLoggedIn) as boolean}
            className={`p-1 rounded-md shadow-sm border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium min-w-[65px] ${
                isActive ? activeClassName : inactiveClassName
            }`}
            title={hasNotLoggedIn ? 'User not synced - cannot change role' : title}
        >
            {isLoading ? <InlineSpinner className="w-3 h-3" /> : label}
        </button>
    );
}

function toNumber(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
}

interface TeamWithMembers {
    id: number;
    name: string;
    members: TeamMemberUser[];
}

export function OrganizationTeamsView() {
    const { orgId } = useParams<{ orgId: string }>();
    const { hasPermission } = useUserPermissions();
    const canManageUsers = hasPermission('manage:users');
    const {
        getOrganizations,
        getOrganizationDatabaseUsers,
        syncAllUnsyncedUsersSSE,
        deactivateUser,
    } = useAccessApi();
    const navigate = useNavigate();
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [orgUsers, setOrgUsers] = useState<EnhancedAuth0User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedTeams, setExpandedTeams] = useState<Set<number>>(new Set());
    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
    const [syncingDataUsers, setSyncingDataUsers] = useState<Set<string>>(new Set());
    const [isRefreshingAfterSync, setIsRefreshingAfterSync] = useState(false);
    const [toast, setToast] = useState<{
        show: boolean;
        message: string;
        type: 'success' | 'error';
    }>({ show: false, message: '', type: 'success' });
    const [showBulkRegisterModal, setShowBulkRegisterModal] = useState(false);
    const [bulkRegisterStatus, setBulkRegisterStatus] = useState<{
        isRunning: boolean;
        totalUsers: number;
        registeredUsers: number;
        currentUser: string | null;
        errors: string[];
    }>({
        isRunning: false,
        totalUsers: 0,
        registeredUsers: 0,
        currentUser: null,
        errors: [],
    });
    const cancelBulkRegisterRef = useRef<(() => void) | null>(null);

    const updateUserTeamRole = useUpdateUserTeamRoleOrgAdmin();
    const removeUserFromTeam = useRemoveUserFromTeamOrgAdmin();
    const syncUserDataMutation = useSyncUserData();

    const setLoading = (key: string, loading: boolean) => {
        setLoadingStates((prev) => ({ ...prev, [key]: loading }));
    };

    const getMemberLoadingKey = (
        member: { subject_id?: string | null; user_id?: string },
        action: string,
        roleId?: number,
    ): string => {
        const memberId = (member.subject_id || member.user_id || '').toString();
        return roleId ? `${action}-${memberId}-${roleId}` : `${action}-${memberId}`;
    };

    // Fetch organization details and users
    useEffect(() => {
        const fetchData = async () => {
            if (!orgId) return;

            try {
                setIsLoading(true);
                setError(null);

                // Get organization details
                const orgs = await getOrganizations();
                const org = orgs.find((o) => o.id === orgId);
                if (!org) {
                    setError('Organization not found.');
                    return;
                }
                setOrganization(org);

                // Get organization users
                const response = await getOrganizationDatabaseUsers(orgId, true);

                setOrgUsers(response.users);
            } catch (err) {
                console.error('Error fetching organization data:', err);
                setError('Failed to load organization data.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshOrgUsers = async () => {
        if (!orgId) {
            console.warn('No orgId available for refresh');
            return;
        }
        try {
            console.log('Fetching updated organization users...');
            const response = await getOrganizationDatabaseUsers(orgId, true);
            console.log('Received updated users:', response.users.length, 'users');
            setOrgUsers(response.users);
        } catch (err) {
            console.error('Error refreshing organization users:', err);
            throw err; // Re-throw to allow caller to handle
        }
    };

    // Group users by teams
    const teamsWithMembers = useMemo((): TeamWithMembers[] => {
        const teamMap = new Map<number, TeamWithMembers>();
        let emptyTeamId: number | null = null;

        // Process each user and their team memberships
        orgUsers.forEach((user) => {
            if (user.team_memberships && user.team_memberships.length > 0) {
                user.team_memberships.forEach((membership: TeamMembership) => {
                    if (!teamMap.has(membership.team_id)) {
                        teamMap.set(membership.team_id, {
                            id: membership.team_id,
                            name: membership.team_name,
                            members: [],
                        });
                        // Track empty team ID
                        if (!membership.team_name || membership.team_name === '') {
                            emptyTeamId = membership.team_id;
                        }
                    }
                    teamMap.get(membership.team_id)!.members.push({
                        ...user,
                        // Add the specific team role for this membership
                        current_team_role_id: toNumber(
                            (membership as unknown as { team_role_id: string | number })
                                .team_role_id,
                        ),
                        current_team_role_name: membership.team_role_name,
                    } as TeamMemberUser);
                });
            }
        });

        // Always ensure empty team exists if there are any users
        // The empty team should always be shown (it's the "All Users" team)
        if (orgUsers.length > 0 && !emptyTeamId) {
            // Find the first empty team from any user's memberships, or create placeholder
            // We'll use a placeholder ID that indicates it needs to be created
            const placeholderId = -999; // Special ID to indicate empty team placeholder
            if (!teamMap.has(placeholderId)) {
                teamMap.set(placeholderId, {
                    id: placeholderId,
                    name: '',
                    members: [],
                });
            }
        }

        // Sort teams: empty name (All Users) first, then alphabetically
        return Array.from(teamMap.values()).sort((a, b) => {
            // Empty name (All Users) always comes first
            if (!a.name || a.name === '') return -1;
            if (!b.name || b.name === '') return 1;
            return a.name.localeCompare(b.name);
        });
    }, [orgUsers]);

    // Get users not in any teams
    const usersNotInAnyTeams = useMemo(() => {
        if (!orgUsers.length) return [];

        return orgUsers.filter((user: EnhancedAuth0User) => {
            return !user.team_memberships || user.team_memberships.length === 0;
        });
    }, [orgUsers]);

    const toggleTeam = (teamId: number) => {
        setExpandedTeams((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(teamId)) {
                newSet.delete(teamId);
            } else {
                newSet.add(teamId);
            }
            return newSet;
        });
    };

    const handleUserClick = (user: EnhancedAuth0User) => {
        // Only navigate for users with subject_id (synced users)
        if (user.subject_id && orgId) {
            navigate(`/admin/${orgId}/user/${user.subject_id}`);
        }
    };

    const handleSetRole = async (teamId: number, userId: string, newRoleId: number) => {
        if (!orgId) return;
        try {
            const loadingKey = `setRole-${userId}-${newRoleId}`;
            setLoading(loadingKey, true);
            await updateUserTeamRole.mutateAsync({
                orgId,
                teamId: teamId.toString(),
                userId,
                team_role_id: newRoleId,
            });
            await refreshOrgUsers();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to update role');
        } finally {
            const loadingKey = `setRole-${userId}-${newRoleId}`;
            setLoading(loadingKey, false);
        }
    };

    const handleRemoveUser = async (teamId: number, userId: string, userName: string) => {
        if (!orgId) return;

        // Find the team to check if it's the "All Users" team
        const team = teamsWithMembers.find((t) => t.id === teamId);
        if (!team || !team.name || team.name === '') {
            alert(
                'Cannot remove user from "All Users" team. This team represents the required company relationship and all users must be in it.',
            );
            return;
        }

        const fullUser = orgUsers.find((u) => u.user_id === userId || u.subject_id === userId);
        if (fullUser?.team_memberships && fullUser.team_memberships.length === 1) {
            alert('Cannot remove user from their only team. Users must be in at least one team.');
            return;
        }

        const confirmed = window.confirm(`Remove "${userName}" from "${team.name}" team?`);
        if (!confirmed) return;

        try {
            setLoading('removeUser', true);
            await removeUserFromTeam.mutateAsync({
                orgId,
                teamId: teamId.toString(),
                userId,
            });
            await refreshOrgUsers();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to remove user');
        } finally {
            setLoading('removeUser', false);
        }
    };

    // Filter unsynced users (those without subject_id)
    // Exclude users that are legacy, not SSO, and have no password (invalid users)
    // Also exclude SSO users (they will be associated on next SSO login)
    const unsyncedUsers = useMemo(() => {
        return orgUsers.filter((user) => {
            // Must be unsynced (no subject_id)
            if (user.subject_id) return false;

            // Exclude SSO users (they will be associated on next SSO login)
            if (user.is_sso === true) return false;

            // Check if user is invalid (legacy, not SSO, no password)
            const isInvalidUser = !user.is_sso && !user.has_password;

            // Exclude invalid users from registration
            return !isInvalidUser;
        });
    }, [orgUsers]);

    // Find users with subject_id that are not in the empty team
    const usersNeedingEmptyTeamRegistration = useMemo(() => {
        return orgUsers.filter((user) => {
            // Must have subject_id (synced)
            if (!user.subject_id) return false;

            // Check if user is in the empty team
            const inEmptyTeam =
                user.team_memberships?.some((m) => !m.team_name || m.team_name === '') || false;

            return !inEmptyTeam;
        });
    }, [orgUsers]);

    // Helper to check if a user is invalid (should show warning but not be registerable)
    const isInvalidUser = (user: EnhancedAuth0User): boolean => {
        // Must be legacy (no subject_id)
        if (user.subject_id) return false;

        // Invalid if: not SSO AND no password
        return !user.is_sso && !user.has_password;
    };

    const handleBulkRegister = async () => {
        if (!orgId) return;
        setShowBulkRegisterModal(true);
        setBulkRegisterStatus({
            isRunning: false,
            totalUsers: unsyncedUsers.length,
            registeredUsers: 0,
            currentUser: null,
            errors: [],
        });
    };

    const handleRegisterToEmptyTeam = async () => {
        if (!orgId) return;

        try {
            setIsRefreshingAfterSync(true);
            setError(null);

            // Use the same SSE endpoint - it handles both unsynced users and empty team registration
            const cancel = await syncAllUnsyncedUsersSSE(
                orgId,
                (data: unknown) => {
                    // Handle progress updates
                    const message = data as {
                        type: string;
                        message?: string;
                        progress?: number;
                        totalUsers?: number;
                        registeredUsers?: number;
                    };
                    if (message.type === 'team_registration_progress') {
                        console.log('Team registration progress:', message);
                    }
                },
                (err: Error) => {
                    console.error('Error registering to empty team:', err);
                    setError(`Failed to register users to empty team: ${err.message}`);
                    setIsRefreshingAfterSync(false);
                },
                async () => {
                    // On complete, refresh the user list
                    try {
                        await refreshOrgUsers();
                    } catch (refreshErr) {
                        console.error('Error refreshing after sync:', refreshErr);
                    } finally {
                        setIsRefreshingAfterSync(false);
                    }
                },
            );

            // Store cancel function
            cancelBulkRegisterRef.current = cancel;
        } catch (err) {
            console.error('Error starting empty team registration:', err);
            setError('Failed to start registration. Please try again.');
            setIsRefreshingAfterSync(false);
        }
    };

    const handleStartBulkRegister = async () => {
        if (!orgId) return;

        setBulkRegisterStatus((prev) => ({
            ...prev,
            isRunning: true,
        }));

        try {
            const cancel = await syncAllUnsyncedUsersSSE(
                orgId,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (data: any) => {
                    switch (data.type) {
                        case 'start':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                totalUsers: data.totalUsers,
                            }));
                            break;

                        case 'user_start':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                currentUser: data.userName || data.userPublicId,
                                registeredUsers: data.progress - 1,
                            }));
                            break;

                        case 'user_complete':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                registeredUsers: data.progress,
                                currentUser: null,
                            }));
                            break;

                        case 'user_error':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                registeredUsers: data.progress,
                                currentUser: null,
                                errors: [
                                    ...prev.errors,
                                    `${data.userName || data.userPublicId}: ${data.error}`,
                                ],
                            }));
                            break;

                        case 'complete':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                isRunning: false,
                                currentUser: null,
                            }));
                            // Refresh the user list
                            refreshOrgUsers();
                            break;

                        case 'error':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                isRunning: false,
                                errors: [...prev.errors, data.error],
                            }));
                            break;
                    }
                },
                (error) => {
                    console.error('Bulk register error:', error);
                    setBulkRegisterStatus((prev) => ({
                        ...prev,
                        isRunning: false,
                        errors: [...prev.errors, error.message],
                    }));
                },
                () => {
                    setBulkRegisterStatus((prev) => ({
                        ...prev,
                        isRunning: false,
                    }));
                },
            );

            cancelBulkRegisterRef.current = cancel;
        } catch (error) {
            console.error('Failed to start bulk register:', error);
            setBulkRegisterStatus((prev) => ({
                ...prev,
                isRunning: false,
                errors: [
                    ...prev.errors,
                    error instanceof Error ? error.message : 'Failed to start bulk register',
                ],
            }));
        }
    };

    const handleCancelBulkRegister = () => {
        const currentStatus = bulkRegisterStatus;
        if (cancelBulkRegisterRef.current) {
            cancelBulkRegisterRef.current();
            cancelBulkRegisterRef.current = null;
        }
        setBulkRegisterStatus((prev) => ({
            ...prev,
            isRunning: false,
            currentUser: null,
        }));
        // Refresh users list if any were registered before cancellation
        if (currentStatus.registeredUsers > 0) {
            refreshOrgUsers();
        }
        setToast({
            show: true,
            message: `Register cancelled. ${currentStatus.registeredUsers} of ${currentStatus.totalUsers} users were registered.`,
            type: 'success',
        });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleCloseBulkRegisterModal = () => {
        if (!bulkRegisterStatus.isRunning) {
            // Refresh users list if any were registered
            if (bulkRegisterStatus.registeredUsers > 0) {
                refreshOrgUsers();
            }
            setShowBulkRegisterModal(false);
            setBulkRegisterStatus({
                isRunning: false,
                totalUsers: 0,
                registeredUsers: 0,
                currentUser: null,
                errors: [],
            });
        }
    };

    const handleDeleteUser = async (userId: string) => {
        if (!orgId) return;

        const user = orgUsers.find((u) => u.user_id === userId);
        if (!user) return;

        // Only allow deletion of legacy users (users without subject_id)
        if (user.subject_id) {
            setToast({
                show: true,
                message:
                    'Cannot delete users that are already synced to Auth0. Only legacy users can be deleted.',
                type: 'error',
            });
            setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
            return;
        }

        // Don't allow deletion of SSO users (they will be associated on next SSO login)
        if (user.is_sso === true) {
            setToast({
                show: true,
                message:
                    'Cannot delete SSO users. They will be associated on next single sign on login.',
                type: 'error',
            });
            setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
            return;
        }

        // Confirm deletion
        if (
            !window.confirm(
                `Are you sure you want to delete ${user.name || user.email}? This will deactivate the user and they will no longer appear in the list.`,
            )
        ) {
            return;
        }

        try {
            // Extract public_id from user_id (could be 'db|publicId' or just 'publicId')
            const userPublicId = user.public_id || userId.replace('db|', '');

            await deactivateUser(orgId, userPublicId);

            setToast({
                show: true,
                message: `User ${user.name || user.email} has been deleted successfully.`,
                type: 'success',
            });
            setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);

            // Refresh the user list
            await refreshOrgUsers();
        } catch (error) {
            console.error('Failed to delete user:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to delete user';
            setToast({
                show: true,
                message: errorMessage,
                type: 'error',
            });
            setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
        }
    };

    const handleSyncUserData = async (userId: string) => {
        try {
            setSyncingDataUsers((prev) => new Set(prev).add(userId));

            // Extract the database user public_id from the user object
            // The sync endpoint expects the user's public_id (database identifier)
            const user = orgUsers.find((u) => u.user_id === userId);
            const userPublicId = user?.public_id;

            if (!userPublicId) {
                throw new Error('User public_id not found - cannot sync user data');
            }

            const result = await syncUserDataMutation.mutateAsync(userPublicId);

            // Show success toast with user info
            const teams = result.user.team_assignment?.teams || [];
            const firstTeam = teams[0];
            const teamName =
                !firstTeam?.name || firstTeam.name === ''
                    ? 'All Users'
                    : firstTeam?.name || 'No team';
            const companyName = firstTeam?.company?.name || 'No company';
            const companySyncNote = result.sync_result.company_was_synced
                ? ' (company synced)'
                : '';

            // Show team count if multiple teams
            const teamDisplayName =
                teams.length > 1 ? `${teamName} (+${teams.length - 1} more teams)` : teamName;

            setToast({
                show: true,
                message: `User registered: ${result.user.first_name} ${result.user.last_name} - ${teamDisplayName} at ${companyName}${companySyncNote}`,
                type: 'success',
            });

            // Auto-hide toast after 3 seconds
            setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);

            console.log('User data sync result:', result);

            // Wait a moment for the sync to propagate, then refresh the user list
            setTimeout(async () => {
                try {
                    setIsRefreshingAfterSync(true);
                    console.log('Refreshing user list after sync...');
                    await refreshOrgUsers();
                    console.log('User list refreshed successfully');
                } catch (refreshError) {
                    console.error('Failed to refresh user list after sync:', refreshError);
                } finally {
                    setIsRefreshingAfterSync(false);
                }
            }, 1000); // 1 second delay
        } catch (error) {
            console.error('Failed to sync user data:', error);
            const errorMessage =
                error instanceof Error ? error.message : 'Failed to sync user data';
            setToast({
                show: true,
                message: errorMessage,
                type: 'error',
            });

            // Auto-hide error toast after 5 seconds
            setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
        } finally {
            setSyncingDataUsers((prev) => {
                const newSet = new Set(prev);
                newSet.delete(userId);
                return newSet;
            });
        }
    };

    // Only show this component if user has administrate:organizations or administrate:users permission
    if (!hasPermission('administrate:organizations') && !hasPermission('administrate:users')) {
        return null;
    }

    if (isLoading) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader className="bg-muted/40">
                        <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">
                            Organization Teams
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <LoadingPage />
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader className="bg-muted/40">
                        <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">
                            Organization Teams
                        </CardTitle>
                    </CardHeader>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Back Button and Organization Header */}
            <Card>
                <CardHeader className="bg-muted/40">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">
                                {organization?.display_name || organization?.name}
                            </CardTitle>
                            <div className="bg-primary text-primary-foreground text-xs font-semibold px-2 py-0.5 rounded-full">
                                {orgUsers.length}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => navigate(`/admin/${orgId}/sso`)}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                            >
                                <svg
                                    className="w-4 h-4 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                </svg>
                                Manage SSO
                            </button>
                        </div>
                    </div>
                    <CardDescription>
                        Users organized by teams. The "All Users" team represents all users in the
                        organization (required). Named teams show additional team memberships. Click
                        on synced users to view details.
                        {isRefreshingAfterSync && (
                            <span className="inline-flex items-center gap-1 ml-2 text-blue-600">
                                <InlineSpinner />
                                Refreshing after sync...
                            </span>
                        )}
                    </CardDescription>
                    <div className="mt-4">
                        <Button
                            onClick={handleBulkRegister}
                            variant="outline"
                            size="sm"
                            className="text-sm"
                            disabled={unsyncedUsers.length === 0}
                        >
                            <svg
                                className="w-4 h-4 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                            </svg>
                            Register All Unsynced Username and Password Users (
                            {unsyncedUsers.length})
                        </Button>
                    </div>
                </CardHeader>

                <CardContent className="p-0">
                    <div className="space-y-0">
                        {/* Teams */}
                        {teamsWithMembers.map((team) => (
                            <div key={team.id} className="border-b border-gray-100 last:border-b-0">
                                <div className="bg-[#f8f9fc] border-b border-gray-100">
                                    <button
                                        onClick={() => toggleTeam(team.id)}
                                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#f0f2f8] transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <svg
                                                className={`w-4 h-4 text-gray-500 transition-transform ${
                                                    expandedTeams.has(team.id) ? 'rotate-90' : ''
                                                }`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 5l7 7-7 7"
                                                />
                                            </svg>
                                            <div className="flex items-center gap-2">
                                                {(!team.name || team.name === '') && (
                                                    <svg
                                                        className="w-5 h-5 text-gray-600"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                                        />
                                                    </svg>
                                                )}
                                                <h3
                                                    className={`text-base font-semibold ${
                                                        !team.name || team.name === ''
                                                            ? 'text-gray-800'
                                                            : 'text-gray-900'
                                                    }`}
                                                >
                                                    {!team.name || team.name === ''
                                                        ? 'All Users'
                                                        : team.name}
                                                </h3>
                                                {(!team.name || team.name === '') && (
                                                    <TooltipProvider delayDuration={0}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div className="flex items-center">
                                                                    <svg
                                                                        className="w-4 h-4 text-gray-500 cursor-help"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={2}
                                                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-xs">
                                                                <p className="text-sm">
                                                                    This represents all users in the
                                                                    organization. Every user must be
                                                                    in this team as it shows their
                                                                    relationship to the company.
                                                                    Users may also belong to
                                                                    additional named teams below.
                                                                </p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                {(!team.name || team.name === '') && (
                                                    <span className="text-xs text-gray-600 bg-gray-200 px-2 py-0.5 rounded font-medium">
                                                        Default
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                                    !team.name || team.name === ''
                                                        ? 'bg-gray-500 text-white'
                                                        : 'bg-blue-500 text-white'
                                                }`}
                                            >
                                                {team.members.length}
                                            </div>
                                        </div>
                                    </button>
                                </div>

                                {expandedTeams.has(team.id) && (
                                    <div className="border-t border-gray-100 bg-white">
                                        {(!team.name || team.name === '') && (
                                            <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                                                <div className="flex items-start justify-between gap-4">
                                                    <p className="text-xs text-blue-800 flex-1">
                                                        <strong>All Users Team:</strong> This team
                                                        represents all users in the organization.
                                                        Every user must be in this team as it shows
                                                        their relationship to the company. Users may
                                                        also belong to additional named teams shown
                                                        below.
                                                    </p>
                                                    {usersNeedingEmptyTeamRegistration.length >
                                                        0 && (
                                                        <Button
                                                            onClick={handleRegisterToEmptyTeam}
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-xs whitespace-nowrap"
                                                            disabled={isRefreshingAfterSync}
                                                        >
                                                            <svg
                                                                className="w-3 h-3 mr-1"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={2}
                                                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                                                />
                                                            </svg>
                                                            Register{' '}
                                                            {
                                                                usersNeedingEmptyTeamRegistration.length
                                                            }{' '}
                                                            User
                                                            {usersNeedingEmptyTeamRegistration.length !==
                                                            1
                                                                ? 's'
                                                                : ''}{' '}
                                                            to Team
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <Table className="w-full table-auto">
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-[25%] px-2 text-xs">
                                                        Member
                                                    </TableHead>
                                                    <TableHead className="w-[25%] px-2 text-xs">
                                                        Email
                                                    </TableHead>
                                                    <TableHead className="w-[20%] px-2 text-xs">
                                                        Role
                                                    </TableHead>
                                                    <TableHead className="w-[10%] px-2 text-xs">
                                                        Status
                                                    </TableHead>
                                                    {canManageUsers && (
                                                        <TableHead className="w-[15%] text-center px-1 text-xs">
                                                            Role Actions
                                                        </TableHead>
                                                    )}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {team.members
                                                    .sort((a, b) => {
                                                        // Sort synced users first
                                                        if (a.subject_id && !b.subject_id)
                                                            return -1;
                                                        if (!a.subject_id && b.subject_id) return 1;
                                                        return a.name.localeCompare(b.name);
                                                    })
                                                    .map((user: TeamMemberUser) => {
                                                        const hasNotLoggedIn = !user.subject_id;
                                                        return (
                                                            <TableRow
                                                                key={user.user_id}
                                                                className={`hover:bg-neutral-50 ${
                                                                    hasNotLoggedIn
                                                                        ? 'opacity-60 bg-gray-50'
                                                                        : ''
                                                                }`}
                                                                onClick={() =>
                                                                    handleUserClick(user)
                                                                }
                                                                title={
                                                                    hasNotLoggedIn
                                                                        ? 'User not yet synced with Auth0'
                                                                        : 'Click to view user details'
                                                                }
                                                            >
                                                                <TableCell>
                                                                    <div className="flex items-center gap-3">
                                                                        {renderUserAvatar(
                                                                            user.name,
                                                                            hasNotLoggedIn,
                                                                        )}
                                                                        <div className="flex-1 min-w-0">
                                                                            <button
                                                                                onClick={() =>
                                                                                    handleUserClick(
                                                                                        user,
                                                                                    )
                                                                                }
                                                                                className="font-medium text-sm text-neutral-900 hover:text-primary-600 transition-colors cursor-pointer text-left flex items-center gap-2"
                                                                                title="View user details"
                                                                            >
                                                                                {user.name}
                                                                                {isInvalidUser(
                                                                                    user,
                                                                                ) && (
                                                                                    <TooltipProvider
                                                                                        delayDuration={
                                                                                            0
                                                                                        }
                                                                                    >
                                                                                        <Tooltip>
                                                                                            <TooltipTrigger
                                                                                                asChild
                                                                                            >
                                                                                                <svg
                                                                                                    className="w-4 h-4 text-orange-500 flex-shrink-0 cursor-help"
                                                                                                    fill="currentColor"
                                                                                                    viewBox="0 0 20 20"
                                                                                                >
                                                                                                    <path
                                                                                                        fillRule="evenodd"
                                                                                                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                                                                        clipRule="evenodd"
                                                                                                    />
                                                                                                </svg>
                                                                                            </TooltipTrigger>
                                                                                            <TooltipContent>
                                                                                                <p>
                                                                                                    This
                                                                                                    user
                                                                                                    cannot
                                                                                                    be
                                                                                                    registered
                                                                                                    because
                                                                                                    they
                                                                                                    are
                                                                                                    not
                                                                                                    configured
                                                                                                    for
                                                                                                    SSO
                                                                                                    and
                                                                                                    do
                                                                                                    not
                                                                                                    have
                                                                                                    a
                                                                                                    password
                                                                                                    set.
                                                                                                </p>
                                                                                            </TooltipContent>
                                                                                        </Tooltip>
                                                                                    </TooltipProvider>
                                                                                )}
                                                                            </button>
                                                                            {/* Show other teams this user belongs to */}
                                                                            {user.team_memberships &&
                                                                                user
                                                                                    .team_memberships
                                                                                    .length > 1 && (
                                                                                    <div className="mt-1 flex flex-wrap gap-1 items-center">
                                                                                        <span className="text-xs text-gray-500">
                                                                                            Also in:
                                                                                        </span>
                                                                                        {user.team_memberships
                                                                                            .filter(
                                                                                                (
                                                                                                    m: TeamMembership,
                                                                                                ) =>
                                                                                                    m.team_id !==
                                                                                                    team.id,
                                                                                            )
                                                                                            .map(
                                                                                                (
                                                                                                    membership: TeamMembership,
                                                                                                ) => (
                                                                                                    <span
                                                                                                        key={
                                                                                                            membership.team_id
                                                                                                        }
                                                                                                        className={`text-xs px-1.5 py-0.5 rounded ${
                                                                                                            !membership.team_name ||
                                                                                                            membership.team_name ===
                                                                                                                ''
                                                                                                                ? 'text-gray-700 bg-gray-200 font-medium'
                                                                                                                : 'text-gray-600 bg-gray-100'
                                                                                                        }`}
                                                                                                        title={`Also in: ${membership.team_name || 'All Users'}`}
                                                                                                    >
                                                                                                        {membership.team_name ||
                                                                                                            'All Users'}
                                                                                                    </span>
                                                                                                ),
                                                                                            )}
                                                                                    </div>
                                                                                )}
                                                                        </div>
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <div className="text-sm text-neutral-600">
                                                                        {user.email}
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <RoleBadge
                                                                        role={getProRoleName(
                                                                            toNumber(
                                                                                user.current_team_role_id,
                                                                            ),
                                                                        )}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <div className="text-xs">
                                                                        {hasNotLoggedIn ? (
                                                                            <span className="text-gray-400">
                                                                                Legacy
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-green-600">
                                                                                Active
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </TableCell>
                                                                {canManageUsers && (
                                                                    <TableCell>
                                                                        <div className="flex items-center justify-center gap-1">
                                                                            {hasNotLoggedIn &&
                                                                            user.is_sso === true ? (
                                                                                <TooltipProvider
                                                                                    delayDuration={
                                                                                        0
                                                                                    }
                                                                                >
                                                                                    <Tooltip>
                                                                                        <TooltipTrigger
                                                                                            asChild
                                                                                        >
                                                                                            <Badge className="w-5 h-5 text-blue-500 cursor-help" />
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent>
                                                                                            <p>
                                                                                                User
                                                                                                will
                                                                                                be
                                                                                                associated
                                                                                                on
                                                                                                next
                                                                                                single
                                                                                                sign
                                                                                                on
                                                                                                login.
                                                                                            </p>
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                </TooltipProvider>
                                                                            ) : hasNotLoggedIn &&
                                                                              !isInvalidUser(
                                                                                  user,
                                                                              ) ? (
                                                                                <div className="flex flex-row gap-2">
                                                                                    <button
                                                                                        onClick={(
                                                                                            e,
                                                                                        ) => {
                                                                                            e.preventDefault();
                                                                                            handleSyncUserData(
                                                                                                user.user_id,
                                                                                            );
                                                                                            e.stopPropagation();
                                                                                        }}
                                                                                        disabled={syncingDataUsers.has(
                                                                                            user.user_id,
                                                                                        )}
                                                                                        className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                                                                            syncingDataUsers.has(
                                                                                                user.user_id,
                                                                                            )
                                                                                                ? 'border-muted text-muted-foreground bg-muted/30'
                                                                                                : 'border-primary/40 text-primary hover:text-primary hover:border-primary bg-primary/10'
                                                                                        }`}
                                                                                        title={
                                                                                            syncingDataUsers.has(
                                                                                                user.user_id,
                                                                                            )
                                                                                                ? 'Registering user...'
                                                                                                : 'Register User'
                                                                                        }
                                                                                    >
                                                                                        {syncingDataUsers.has(
                                                                                            user.user_id,
                                                                                        ) ? (
                                                                                            <>
                                                                                                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
                                                                                                Registering...
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <svg
                                                                                                    className="w-3 h-3 mr-1"
                                                                                                    fill="none"
                                                                                                    stroke="currentColor"
                                                                                                    viewBox="0 0 24 24"
                                                                                                >
                                                                                                    <path
                                                                                                        strokeLinecap="round"
                                                                                                        strokeLinejoin="round"
                                                                                                        strokeWidth={
                                                                                                            2
                                                                                                        }
                                                                                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                                                                    />
                                                                                                </svg>
                                                                                                Register
                                                                                            </>
                                                                                        )}
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={(
                                                                                            e,
                                                                                        ) => {
                                                                                            e.preventDefault();
                                                                                            handleDeleteUser(
                                                                                                user.user_id,
                                                                                            );
                                                                                            e.stopPropagation();
                                                                                        }}
                                                                                        className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 hover:text-red-800 hover:border-red-400 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                                                                        title="Delete User"
                                                                                    >
                                                                                        <svg
                                                                                            className="w-3 h-3 mr-1"
                                                                                            fill="none"
                                                                                            stroke="currentColor"
                                                                                            viewBox="0 0 24 24"
                                                                                        >
                                                                                            <path
                                                                                                strokeLinecap="round"
                                                                                                strokeLinejoin="round"
                                                                                                strokeWidth={
                                                                                                    2
                                                                                                }
                                                                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                                            />
                                                                                        </svg>
                                                                                        Delete
                                                                                    </button>
                                                                                </div>
                                                                            ) : hasNotLoggedIn &&
                                                                              isInvalidUser(
                                                                                  user,
                                                                              ) ? (
                                                                                <button
                                                                                    onClick={(
                                                                                        e,
                                                                                    ) => {
                                                                                        e.preventDefault();
                                                                                        handleDeleteUser(
                                                                                            user.user_id,
                                                                                        );
                                                                                        e.stopPropagation();
                                                                                    }}
                                                                                    className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 hover:text-red-800 hover:border-red-400 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                                                                    title="Delete User"
                                                                                >
                                                                                    <svg
                                                                                        className="w-3 h-3 mr-1"
                                                                                        fill="none"
                                                                                        stroke="currentColor"
                                                                                        viewBox="0 0 24 24"
                                                                                    >
                                                                                        <path
                                                                                            strokeLinecap="round"
                                                                                            strokeLinejoin="round"
                                                                                            strokeWidth={
                                                                                                2
                                                                                            }
                                                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                                        />
                                                                                    </svg>
                                                                                    Delete
                                                                                </button>
                                                                            ) : (
                                                                                <>
                                                                                    {PRO_TEAM_ROLE_BUTTONS.map(
                                                                                        (
                                                                                            roleButton,
                                                                                        ) => (
                                                                                            <TeamMemberRoleButton
                                                                                                key={
                                                                                                    roleButton.roleId
                                                                                                }
                                                                                                teamId={
                                                                                                    team.id
                                                                                                }
                                                                                                user={
                                                                                                    user
                                                                                                }
                                                                                                roleId={
                                                                                                    roleButton.roleId
                                                                                                }
                                                                                                label={
                                                                                                    roleButton.label
                                                                                                }
                                                                                                activeClassName={
                                                                                                    roleButton.activeClassName
                                                                                                }
                                                                                                inactiveClassName={
                                                                                                    roleButton.inactiveClassName
                                                                                                }
                                                                                                title={
                                                                                                    roleButton.title
                                                                                                }
                                                                                                loadingStates={
                                                                                                    loadingStates
                                                                                                }
                                                                                                getMemberLoadingKey={
                                                                                                    getMemberLoadingKey
                                                                                                }
                                                                                                onSetRole={
                                                                                                    handleSetRole
                                                                                                }
                                                                                                hasNotLoggedIn={
                                                                                                    hasNotLoggedIn
                                                                                                }
                                                                                            />
                                                                                        ),
                                                                                    )}

                                                                                    {user.subject_id ? (
                                                                                        <button
                                                                                            onClick={() =>
                                                                                                handleRemoveUser(
                                                                                                    team.id,
                                                                                                    (user.subject_id ||
                                                                                                        user.user_id) as string,
                                                                                                    user.name,
                                                                                                )
                                                                                            }
                                                                                            disabled={
                                                                                                loadingStates.removeUser ||
                                                                                                !team.name ||
                                                                                                team.name ===
                                                                                                    '' ||
                                                                                                (() => {
                                                                                                    const fullUser =
                                                                                                        orgUsers.find(
                                                                                                            (
                                                                                                                u,
                                                                                                            ) =>
                                                                                                                u.user_id ===
                                                                                                                    (user.subject_id ||
                                                                                                                        user.user_id) ||
                                                                                                                u.subject_id ===
                                                                                                                    (user.subject_id ||
                                                                                                                        user.user_id),
                                                                                                        );
                                                                                                    return (
                                                                                                        fullUser
                                                                                                            ?.team_memberships
                                                                                                            ?.length ===
                                                                                                        1
                                                                                                    );
                                                                                                })()
                                                                                            }
                                                                                            className="p-1.5 bg-white rounded-md shadow-sm border border-neutral-200 text-red-600 hover:text-red-700 hover:border-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                            title={(() => {
                                                                                                if (
                                                                                                    !team.name ||
                                                                                                    team.name ===
                                                                                                        ''
                                                                                                ) {
                                                                                                    return 'Cannot remove user from "All Users" team - this is required';
                                                                                                }
                                                                                                const fullUser =
                                                                                                    orgUsers.find(
                                                                                                        (
                                                                                                            u,
                                                                                                        ) =>
                                                                                                            u.user_id ===
                                                                                                                (user.subject_id ||
                                                                                                                    user.user_id) ||
                                                                                                            u.subject_id ===
                                                                                                                (user.subject_id ||
                                                                                                                    user.user_id),
                                                                                                    );
                                                                                                return fullUser
                                                                                                    ?.team_memberships
                                                                                                    ?.length ===
                                                                                                    1
                                                                                                    ? 'Cannot remove user from their only team'
                                                                                                    : 'Remove from team';
                                                                                            })()}
                                                                                        >
                                                                                            {loadingStates.removeUser ? (
                                                                                                <InlineSpinner className="w-3.5 h-3.5" />
                                                                                            ) : (
                                                                                                <svg
                                                                                                    className="w-3.5 h-3.5"
                                                                                                    fill="none"
                                                                                                    stroke="currentColor"
                                                                                                    viewBox="0 0 24 24"
                                                                                                >
                                                                                                    <path
                                                                                                        strokeLinecap="round"
                                                                                                        strokeLinejoin="round"
                                                                                                        strokeWidth={
                                                                                                            2
                                                                                                        }
                                                                                                        d="M6 18L18 6M6 6l12 12"
                                                                                                    />
                                                                                                </svg>
                                                                                            )}
                                                                                        </button>
                                                                                    ) : (
                                                                                        <div className="p-1.5 w-[30px] h-[30px]" />
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        );
                                                    })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Users not in any teams */}
                        {usersNotInAnyTeams.length > 0 && (
                            <div className="border-b border-gray-100 last:border-b-0">
                                <div className="bg-blue-50 border-b border-blue-200">
                                    <div className="px-4 py-3 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <svg
                                                className="w-4 h-4 text-blue-500"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 5l7 7-7 7"
                                                />
                                            </svg>
                                            <h3 className="text-base font-semibold text-blue-900">
                                                Users not in any teams
                                            </h3>
                                            <div className="bg-blue-500 text-white text-xs font-semibold px-2 py-1 rounded-full">
                                                {usersNotInAnyTeams.length}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t border-blue-200 bg-blue-25">
                                    <Table className="w-full table-auto">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-[40%] px-4 text-xs">
                                                    User
                                                </TableHead>
                                                <TableHead className="w-[30%] px-4 text-xs">
                                                    Email
                                                </TableHead>
                                                <TableHead className="w-[15%] px-4 text-xs">
                                                    Role
                                                </TableHead>
                                                <TableHead className="w-[10%] px-4 text-xs">
                                                    Status
                                                </TableHead>
                                                {canManageUsers && (
                                                    <TableHead className="w-[15%] text-center px-4 text-xs">
                                                        Actions
                                                    </TableHead>
                                                )}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {usersNotInAnyTeams
                                                .sort((a, b) => {
                                                    // Sort synced users first
                                                    if (a.subject_id && !b.subject_id) return -1;
                                                    if (!a.subject_id && b.subject_id) return 1;
                                                    return a.name.localeCompare(b.name);
                                                })
                                                .map((user: EnhancedAuth0User) => {
                                                    const hasNotLoggedIn = !user.subject_id;
                                                    return (
                                                        <TableRow
                                                            key={user.user_id}
                                                            className={`${
                                                                hasNotLoggedIn
                                                                    ? 'opacity-50'
                                                                    : 'hover:bg-gray-50 cursor-pointer'
                                                            }`}
                                                            onClick={() => handleUserClick(user)}
                                                            title={
                                                                hasNotLoggedIn
                                                                    ? 'User not yet synced with Auth0'
                                                                    : 'Click to view user details'
                                                            }
                                                        >
                                                            <TableCell className="px-4 py-3">
                                                                <div className="flex items-center gap-3">
                                                                    {renderUserAvatar(
                                                                        user.name,
                                                                        hasNotLoggedIn,
                                                                    )}
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="font-medium text-sm text-gray-900 truncate flex items-center gap-2">
                                                                            {user.name}
                                                                            {isInvalidUser(
                                                                                user,
                                                                            ) && (
                                                                                <TooltipProvider
                                                                                    delayDuration={
                                                                                        0
                                                                                    }
                                                                                >
                                                                                    <Tooltip>
                                                                                        <TooltipTrigger
                                                                                            asChild
                                                                                        >
                                                                                            <svg
                                                                                                className="w-4 h-4 text-orange-500 flex-shrink-0 cursor-help"
                                                                                                fill="currentColor"
                                                                                                viewBox="0 0 20 20"
                                                                                            >
                                                                                                <path
                                                                                                    fillRule="evenodd"
                                                                                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                                                                                    clipRule="evenodd"
                                                                                                />
                                                                                            </svg>
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent>
                                                                                            <p>
                                                                                                This
                                                                                                user
                                                                                                cannot
                                                                                                be
                                                                                                registered
                                                                                                because
                                                                                                they
                                                                                                are
                                                                                                not
                                                                                                configured
                                                                                                for
                                                                                                SSO
                                                                                                and
                                                                                                do
                                                                                                not
                                                                                                have
                                                                                                a
                                                                                                password
                                                                                                set.
                                                                                            </p>
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                </TooltipProvider>
                                                                            )}
                                                                        </div>
                                                                        {hasNotLoggedIn && (
                                                                            <div className="text-xs text-gray-400">
                                                                                Not synced
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="px-4 py-3">
                                                                <div className="text-sm text-gray-600 truncate">
                                                                    {user.email}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="px-4 py-3">
                                                                <span className="text-xs text-gray-400">
                                                                    —
                                                                </span>
                                                            </TableCell>
                                                            <TableCell className="px-4 py-3">
                                                                <div className="text-xs">
                                                                    {hasNotLoggedIn ? (
                                                                        <span className="text-gray-400">
                                                                            Legacy
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-orange-600">
                                                                            No Team
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            {canManageUsers && (
                                                                <TableCell className="px-4 py-3">
                                                                    <div className="flex items-center justify-center gap-2">
                                                                        {hasNotLoggedIn &&
                                                                        user.is_sso === true ? (
                                                                            <TooltipProvider
                                                                                delayDuration={0}
                                                                            >
                                                                                <Tooltip>
                                                                                    <TooltipTrigger
                                                                                        asChild
                                                                                    >
                                                                                        <Badge className="w-5 h-5 text-blue-500 cursor-help" />
                                                                                    </TooltipTrigger>
                                                                                    <TooltipContent>
                                                                                        <p>
                                                                                            User
                                                                                            will be
                                                                                            associated
                                                                                            on next
                                                                                            single
                                                                                            sign on
                                                                                            login.
                                                                                        </p>
                                                                                    </TooltipContent>
                                                                                </Tooltip>
                                                                            </TooltipProvider>
                                                                        ) : hasNotLoggedIn &&
                                                                          !isInvalidUser(user) ? (
                                                                            <>
                                                                                <button
                                                                                    onClick={(
                                                                                        e,
                                                                                    ) => {
                                                                                        e.preventDefault();
                                                                                        handleSyncUserData(
                                                                                            user.user_id,
                                                                                        );
                                                                                        e.stopPropagation();
                                                                                    }}
                                                                                    disabled={syncingDataUsers.has(
                                                                                        user.user_id,
                                                                                    )}
                                                                                    className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                                                                        syncingDataUsers.has(
                                                                                            user.user_id,
                                                                                        )
                                                                                            ? 'border-muted text-muted-foreground bg-muted/30'
                                                                                            : 'border-primary/40 text-primary hover:text-primary hover:border-primary bg-primary/10'
                                                                                    }`}
                                                                                    title={
                                                                                        syncingDataUsers.has(
                                                                                            user.user_id,
                                                                                        )
                                                                                            ? 'Registering user...'
                                                                                            : 'Register User'
                                                                                    }
                                                                                >
                                                                                    {syncingDataUsers.has(
                                                                                        user.user_id,
                                                                                    ) ? (
                                                                                        <>
                                                                                            <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
                                                                                            Registering...
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <svg
                                                                                                className="w-3 h-3 mr-1"
                                                                                                fill="none"
                                                                                                stroke="currentColor"
                                                                                                viewBox="0 0 24 24"
                                                                                            >
                                                                                                <path
                                                                                                    strokeLinecap="round"
                                                                                                    strokeLinejoin="round"
                                                                                                    strokeWidth={
                                                                                                        2
                                                                                                    }
                                                                                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                                                                />
                                                                                            </svg>
                                                                                            Register
                                                                                        </>
                                                                                    )}
                                                                                </button>
                                                                                <button
                                                                                    onClick={(
                                                                                        e,
                                                                                    ) => {
                                                                                        e.preventDefault();
                                                                                        handleDeleteUser(
                                                                                            user.user_id,
                                                                                        );
                                                                                        e.stopPropagation();
                                                                                    }}
                                                                                    className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 hover:text-red-800 hover:border-red-400 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                                                                    title="Delete User"
                                                                                >
                                                                                    <svg
                                                                                        className="w-3 h-3 mr-1"
                                                                                        fill="none"
                                                                                        stroke="currentColor"
                                                                                        viewBox="0 0 24 24"
                                                                                    >
                                                                                        <path
                                                                                            strokeLinecap="round"
                                                                                            strokeLinejoin="round"
                                                                                            strokeWidth={
                                                                                                2
                                                                                            }
                                                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                                        />
                                                                                    </svg>
                                                                                    Delete
                                                                                </button>
                                                                            </>
                                                                        ) : hasNotLoggedIn &&
                                                                          isInvalidUser(user) ? (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleDeleteUser(
                                                                                        user.user_id,
                                                                                    );
                                                                                    e.stopPropagation();
                                                                                }}
                                                                                className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded-md text-red-700 hover:text-red-800 hover:border-red-400 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                                                                title="Delete User"
                                                                            >
                                                                                <svg
                                                                                    className="w-3 h-3 mr-1"
                                                                                    fill="none"
                                                                                    stroke="currentColor"
                                                                                    viewBox="0 0 24 24"
                                                                                >
                                                                                    <path
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        strokeWidth={
                                                                                            2
                                                                                        }
                                                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                                    />
                                                                                </svg>
                                                                                Delete
                                                                            </button>
                                                                        ) : (
                                                                            <span className="text-xs text-gray-400">
                                                                                —
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        )}

                        {/* Empty state */}
                        {teamsWithMembers.length === 0 && usersNotInAnyTeams.length === 0 && (
                            <div className="p-6 text-center text-gray-600">
                                No users found in this organization.
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Toast Notification */}
            {toast.show && (
                <div className="fixed top-4 right-4 z-50">
                    <div
                        className={`flex items-center px-4 py-3 rounded-lg shadow-lg max-w-sm ${
                            toast.type === 'success'
                                ? 'bg-green-50 text-green-800 border border-green-200'
                                : 'bg-red-50 text-red-800 border border-red-200'
                        }`}
                    >
                        {toast.type === 'success' ? (
                            <svg
                                className="w-5 h-5 mr-2 flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        ) : (
                            <svg
                                className="w-5 h-5 mr-2 flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        )}
                        <span className="text-sm font-medium">{toast.message}</span>
                        <button
                            onClick={() => setToast({ show: false, message: '', type: 'success' })}
                            className="ml-3 text-gray-400 hover:text-gray-600"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk Register Modal */}
            {showBulkRegisterModal &&
                createPortal(
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                            <h3 className="text-lg font-semibold mb-4">
                                Register All Unsynced Username and Password Users
                            </h3>

                            <div className="space-y-4">
                                {!bulkRegisterStatus.isRunning &&
                                    bulkRegisterStatus.registeredUsers === 0 && (
                                        <div className="text-center py-4">
                                            <p className="text-sm text-muted-foreground mb-4">
                                                Found{' '}
                                                {bulkRegisterStatus.totalUsers ||
                                                    unsyncedUsers.length}{' '}
                                                users to register.
                                            </p>
                                            <Button
                                                onClick={handleStartBulkRegister}
                                                className="w-full"
                                            >
                                                Start Register
                                            </Button>
                                        </div>
                                    )}

                                {bulkRegisterStatus.isRunning && (
                                    <>
                                        <div>
                                            <div className="flex justify-between text-sm text-gray-600 mb-1">
                                                <span>
                                                    Progress: {bulkRegisterStatus.registeredUsers} /{' '}
                                                    {bulkRegisterStatus.totalUsers}
                                                </span>
                                                <span>
                                                    {bulkRegisterStatus.totalUsers > 0
                                                        ? Math.round(
                                                              (bulkRegisterStatus.registeredUsers /
                                                                  bulkRegisterStatus.totalUsers) *
                                                                  100,
                                                          )
                                                        : 0}
                                                    %
                                                </span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                    style={{
                                                        width: `${
                                                            bulkRegisterStatus.totalUsers > 0
                                                                ? Math.round(
                                                                      (bulkRegisterStatus.registeredUsers /
                                                                          bulkRegisterStatus.totalUsers) *
                                                                          100,
                                                                  )
                                                                : 0
                                                        }%`,
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {bulkRegisterStatus.currentUser && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                                    <span className="text-sm font-medium text-blue-800">
                                                        Registering {bulkRegisterStatus.currentUser}{' '}
                                                        ({bulkRegisterStatus.registeredUsers + 1} of{' '}
                                                        {bulkRegisterStatus.totalUsers})
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {bulkRegisterStatus.errors.length > 0 && (
                                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                                <h4 className="text-sm font-semibold text-red-800 mb-1">
                                                    Errors ({bulkRegisterStatus.errors.length}):
                                                </h4>
                                                <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                                                    {bulkRegisterStatus.errors
                                                        .slice(-5)
                                                        .map((error, index) => (
                                                            <li key={index}>{error}</li>
                                                        ))}
                                                </ul>
                                            </div>
                                        )}
                                    </>
                                )}

                                {!bulkRegisterStatus.isRunning &&
                                    bulkRegisterStatus.totalUsers > 0 &&
                                    bulkRegisterStatus.registeredUsers > 0 && (
                                        <div className="bg-green-50 border border-green-200 rounded-md p-3">
                                            <p className="text-sm text-green-800">
                                                Register completed!{' '}
                                                {bulkRegisterStatus.registeredUsers} of{' '}
                                                {bulkRegisterStatus.totalUsers} users registered
                                                successfully.
                                            </p>
                                        </div>
                                    )}

                                <div className="flex justify-end space-x-2 mt-6">
                                    {bulkRegisterStatus.isRunning ? (
                                        <Button
                                            onClick={handleCancelBulkRegister}
                                            variant="outline"
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            Cancel
                                        </Button>
                                    ) : (
                                        <Button onClick={handleCloseBulkRegisterModal}>
                                            Close
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
}
